import { ABOUT_ME_FORM_TWO_DATA_TYPE, ABOUT_ME_FORM_TYPE, LocationIds, LocationType, MissingEntityLocationType, NBLocationDetails, NBLocationSearchData, UserLocation } from './../about-me.types';
import { EnrichedProfile } from '../about-me.types';
import { config } from "./../../../shared/config";
import { ABOUT_ME_INTIAL_VALUE, ABOUT_ME_LOCATION_DETAILS_INITIAL_VALUES, ABOUT_ME_PERSONAL_DETAILS_INITIAL_VALUES } from '../constants/about-me.contants';
import { getAndroidAppVersion, getPrefsBoolean, getUserData } from '../../../shared/appInterface';
import { is } from 'immer/dist/internal';
import { CITIES_STATUS } from '../../../shared/constants';
import { useWebQueryParamStore } from '../../../shared/stores/query-param-store';

export const getAge = (dateString: string | number | Date) => {
  const today = new Date();
  const birthDate = new Date(dateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Helper :
export const transformDataToAboutFormData = (data: EnrichedProfile) => {
    const tranformedAboutMeData = JSON.parse(
        JSON.stringify(ABOUT_ME_INTIAL_VALUE)
    ) as ABOUT_ME_FORM_TYPE;
    tranformedAboutMeData.personalDetails.name = data?.full_name;
    tranformedAboutMeData.personalDetails.email = data?.contact_info?.email;
    tranformedAboutMeData.personalDetails.dob = data?.date_of_birth;
    tranformedAboutMeData.personalDetails.gender = data?.gender;
    tranformedAboutMeData.locationDetails.city = {
        ...data?.city,
        data: { city: { status: data?.city?.status } },
    };
    tranformedAboutMeData.locationDetails.area = data.area;
    if (data.city) {
        tranformedAboutMeData.locationDetails.isPrefilled = true;
    }
    tranformedAboutMeData.locationDetails.displayName = !!data?.user_location ? data?.user_location?.display_name : data.area ? `${data.area?.name}, ${data.city?.name}` : data.city?.name;
    tranformedAboutMeData.isNBLocationMigrated = data?.user_location?.source === "NB";
    if (data?.user_location?.source === "NB") {
        tranformedAboutMeData.nb_locationDetails = convertToNBLocationDataFormat(
            InputNBLocationMapperType.UserLocationData,
            data.user_location
        );
        if(data?.user_location?.city || data?.user_location?.district || data?.user_location?.sub_district){
          tranformedAboutMeData.nb_locationDetails.isPrefilled = true;
        }
    }
    if (data.full_name) {
        tranformedAboutMeData.personalDetails.isPrefilled = true;
    }
    return tranformedAboutMeData;
};

export const isNBLocationBlockingNudgeFlow = () => {
  const { isNBNudgeEnabled } = useWebQueryParamStore();
  return isNBNudgeEnabled;
};

/** 
 * NB Location migration check to decide wether to handle NBFlow or not: 
 * (Combination of blocking nudge check from android app or android version check)
 * */
export const isNBLocationEnabled = () => {
  // NextBillion location enable logic:
  return getAndroidAppVersion() > config.appVersionFeatureChecks.NBlocation;
}

export const checkIfValidFile = ({
    fileObj,
    onReceiveValidFile,
    onFileSizeError,
    onFormatError,
}: {
    fileObj: File | null | undefined;
    onReceiveValidFile: (file: File) => void;
    onFileSizeError: () => void;
    onFormatError: () => void;
}) => {
    if (!fileObj) {
        return null;
    } else {
        const fileType = fileObj.type;
        const sizeLimit = 5 * 1000 * 1000;
        const mimeTypes = [
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/pdf",
        ];
        if (!mimeTypes.includes(fileType)) {
            onFormatError();
            return;
        }
        if (fileObj.size > sizeLimit) {
            onFileSizeError();
            return;
        }
    }
    onReceiveValidFile(fileObj);
};
export const areObjectsEqual = (obj1?: Record<string, any>, obj2?: Record<string, any>): boolean => {
  if (!obj1 && !obj2) {
    return true;
  }
  if (!obj1 || !obj2) {
    return false;
  }
  // Get the keys from both objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
}

export const convertToNBLocationData = (data: LocationType): NBLocationSearchData => {
  const result: NBLocationSearchData = {
      name: data.name,
      city: {
          status: data.is_live ? CITIES_STATUS.LIVE : CITIES_STATUS.NON_LIVE,
      },
      location_ids: {},
  };
  if (data.type?.toLowerCase() === "city") {
      result.location_ids.city_id = data.id;
  } else if (data.type?.toLowerCase() === "subdistrict") {
      result.location_ids.sub_district_id = data.id;
  } else {
      result.location_ids.area_id = data.id;
  }
  return result;
}

export enum InputNBLocationMapperType {
  AboutmeFormTwoData,
  MissingEntityLocationData,
  UserLocationData,
}

export const convertToNBLocationDataFormat = (inputType: InputNBLocationMapperType, inputData: any): NBLocationDetails => {
  const defaultData:NBLocationDetails  = {
    location_ids: {},
    cityStatus: 1,
    displayName: "",
    isPrefilled:false
  };

  if (inputType === InputNBLocationMapperType.AboutmeFormTwoData) {
    const data = inputData as ABOUT_ME_FORM_TWO_DATA_TYPE;
    if (!data || !data?.city) {
      return defaultData
    }
    const result: NBLocationDetails = {
      location_ids: {
        city_id: data.city?.id,
        area_id: data.area?.id,
      },
      cityStatus: data.city?.status || CITIES_STATUS.NON_LIVE,
      displayName: data.displayName || "",
      isPrefilled: true,
    }
    return result;
  } else
    if (inputType === InputNBLocationMapperType.UserLocationData) {
      const data = inputData as UserLocation;
      if (!data || (!data?.city && !data.district && !data.sub_district)) {
        return defaultData;
      }
      const result: NBLocationDetails = {
        location_ids: {
          city_id: data.city?.id,
          area_id: data.area?.id,
          sub_district_id: data?.sub_district?.id,
        },
        cityStatus: data?.city?.is_live ? CITIES_STATUS.LIVE : CITIES_STATUS.NON_LIVE,
        displayName: data.display_name || "",
        isPrefilled: true,
      }
      return result;
    } else
      if (inputType === InputNBLocationMapperType.MissingEntityLocationData) {
        const data = inputData as MissingEntityLocationType;
        if (!data || !data?.user_location) {
          return defaultData;
        }
        const result: NBLocationDetails = {
          location_ids: {
            city_id: data.user_location?.city_id,
            area_id: data.user_location?.area_id,
            sub_district_id: data.user_location?.sub_district_id
          },
          cityStatus: data?.is_live_city ? CITIES_STATUS.LIVE : CITIES_STATUS.NON_LIVE,
          displayName: data.slug,
          isPrefilled: true,
        }
        return result;
      }
  return defaultData;
}

export const isNBLocationTypeData = (data: any): data is NBLocationDetails => {
  return "name" in data.city && "location_ids" in data.city;
};

export const getLocationType = (
    location_ids: LocationIds
): string => {
    if (!!location_ids?.city_id && location_ids?.area_id) {
        return "city_area";
    } else if (!!location_ids?.city_id) {
        return "city";
    } else if (!!location_ids?.sub_district_id) {
        return "sub_district";
    } else {
      return "";
    }
};

export const getNBAreaName = (
  displayName: string
): string => {
  return displayName.indexOf(",") > -1 ? displayName.split(",")[0] : displayName;
};

export const getNBCityName = (
  displayName: string
): string => {
  return displayName.indexOf(",") > -1 ? displayName.split(",").length > 2 ? displayName.split(",")[1].trim(): displayName.split(",")[0].trim() : displayName;
};

export const checkUserIdExistForResumeParseFeat = (): boolean => {
  return false;
  // try {
  //     const userId = getUserData()?.id?.trim();
  //     return (
  //         !!userId &&
  //         !Number.isNaN(Number(userId.slice(-2))) &&
  //         Number(userId.slice(-2)) >= config.enabledResumeParserUserIdRange.min &&
  //         Number(userId.slice(-2)) <= config.enabledResumeParserUserIdRange.max
  //     );
  // } catch (e) {
  //   if (config.env === "test") return true;
  //   return false;
  // }
}

export const isNewLanguageScreenRevampApplicableInApp = () => {
  /**
   * Combine check of version code & feature flag to decide makemycard screen should show or not.
   */
    return getAndroidAppVersion() >= config.appVersionFeatureChecks.LanguageScreenRevamp && (getPrefsBoolean("onboarding_language_v3_enabled") || false);
} 
